import React, { useState } from 'react';
import { CheckCircleIcon, ArrowRightIcon, BrainIcon, LayersIcon, GitBranchIcon, ShieldIcon, EyeIcon, BookOpenIcon, HelpCircleIcon, ChevronDownIcon, ChevronRightIcon, ZapIcon, TargetIcon, TrendingUpIcon } from 'lucide-react';
interface StepProps {
  number: number;
  title: string;
  description: string;
  details: string[];
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  borderColor: string;
}
function PipelineStep({
  number,
  title,
  description,
  details,
  icon,
  color,
  bgColor,
  borderColor
}: StepProps) {
  const [expanded, setExpanded] = useState(false);
  return <div className={`rounded-xl border ${borderColor} bg-white overflow-hidden`} data-id="element-3145">
      <button className="flex w-full items-start gap-4 p-5 text-left hover:bg-gray-50/50 transition-colors" onClick={() => setExpanded(!expanded)} data-id="element-3146">
        <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${bgColor}`} data-id="element-3147">
          <span className={`font-bold text-sm ${color}`} data-id="element-3148">{number}</span>
        </div>
        <div className="flex-1 min-w-0" data-id="element-3149">
          <div className="flex items-center gap-2 mb-1" data-id="element-3150">
            <span className={`${color}`} data-id="element-3151">{icon}</span>
            <h3 className="font-semibold text-gray-800" data-id="element-3152">{title}</h3>
          </div>
          <p className="text-sm text-gray-600" data-id="element-3153">{description}</p>
        </div>
        <div className="flex-shrink-0 text-gray-400 mt-1" data-id="element-3154">
          {expanded ? <ChevronDownIcon className="h-4 w-4" data-id="element-3155" /> : <ChevronRightIcon className="h-4 w-4" data-id="element-3156" />}
        </div>
      </button>
      {expanded && <div className={`border-t ${borderColor} ${bgColor} px-5 py-4`} data-id="element-3157">
          <ul className="space-y-2" data-id="element-3158">
            {details.map((detail, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3159">
                <CheckCircleIcon className={`h-4 w-4 flex-shrink-0 mt-0.5 ${color}`} data-id="element-3160" />
                {detail}
              </li>)}
          </ul>
        </div>}
    </div>;
}
interface LevelCardProps {
  level: number;
  name: string;
  description: string;
  example: string;
  color: string;
  bgColor: string;
}
function CognitiveLevelCard({
  level,
  name,
  description,
  example,
  color,
  bgColor
}: LevelCardProps) {
  return <div className={`rounded-lg border border-gray-100 ${bgColor} p-4`} data-id="element-3161">
      <div className="flex items-center gap-3 mb-2" data-id="element-3162">
        <div className={`flex h-8 w-8 items-center justify-center rounded-full ${color} bg-white font-bold text-sm shadow-sm`} data-id="element-3163">
          {level}
        </div>
        <h4 className={`font-semibold ${color}`} data-id="element-3164">{name}</h4>
      </div>
      <p className="text-xs text-gray-600 mb-2" data-id="element-3165">{description}</p>
      <p className="text-xs text-gray-500 italic" data-id="element-3166">Voorbeeld: {example}</p>
    </div>;
}
interface FaqItemProps {
  question: string;
  answer: string;
}
function FaqItem({
  question,
  answer
}: FaqItemProps) {
  const [open, setOpen] = useState(false);
  return <div className="border-b border-gray-100 last:border-0" data-id="element-3167">
      <button className="flex w-full items-center justify-between py-4 text-left" onClick={() => setOpen(!open)} data-id="element-3168">
        <span className="font-medium text-gray-800" data-id="element-3169">{question}</span>
        <span className="flex-shrink-0 text-gray-400" data-id="element-3170">
          {open ? <ChevronDownIcon className="h-4 w-4" data-id="element-3171" /> : <ChevronRightIcon className="h-4 w-4" data-id="element-3172" />}
        </span>
      </button>
      {open && <p className="pb-4 text-sm text-gray-600 leading-relaxed" data-id="element-3173">{answer}</p>}
    </div>;
}
export function GovernanceHowItWorksTab() {
  const [activeSection, setActiveSection] = useState<'pipeline' | 'cognitive' | 'didactic' | 'ldg' | 'faq'>('pipeline');
  const sections = [{
    id: 'pipeline' as const,
    label: 'De Pipeline',
    icon: <ZapIcon className="h-4 w-4" data-id="element-3174" />
  }, {
    id: 'cognitive' as const,
    label: 'Cognitieve Niveaus',
    icon: <BrainIcon className="h-4 w-4" data-id="element-3175" />
  }, {
    id: 'didactic' as const,
    label: 'Didactische Diepte',
    icon: <LayersIcon className="h-4 w-4" data-id="element-3176" />
  }, {
    id: 'ldg' as const,
    label: 'LDG-Graaf',
    icon: <GitBranchIcon className="h-4 w-4" data-id="element-3177" />
  }, {
    id: 'faq' as const,
    label: 'Veelgestelde Vragen',
    icon: <HelpCircleIcon className="h-4 w-4" data-id="element-3178" />
  }];
  const pipelineSteps: StepProps[] = [{
    number: 1,
    title: 'Schema Validatie',
    description: 'Controleert of de JSON-structuur van de vraag correct en volledig is.',
    details: ['Verplichte velden aanwezig: id, familyId, questionType, question, tier, levelCode, group, skill, subSkill', 'Het vraag-ID is uniek en volgt het formaat FAMILYID-NNN', 'Tier en levelCode zijn consistent met de taxonomy registry', 'Module, skill en subSkill komen exact overeen met de taxonomy definitie', 'FamilyId is geregistreerd in de taxonomy (geen onbekende families)'],
    icon: <ShieldIcon className="h-4 w-4" data-id="element-3179" />,
    color: 'text-slate-600',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-200'
  }, {
    number: 2,
    title: 'Vraagtype Validatie',
    description: 'Controleert of het vraagtype geldig is en het antwoordformaat klopt.',
    details: ['Het vraagtype is geregistreerd in het vraagtype-register (questionTypeRegistry)', 'Meerkeuze-vragen hebben minimaal 2 en maximaal 5 opties', 'Het correctAnswer-veld heeft het juiste formaat voor het vraagtype', 'Interactieve typen (clock, money, ratio_table) hebben geldige data-objecten', 'Verouderd interactiveConfig-veld is niet aanwezig (strictDataConvention)'],
    icon: <TargetIcon className="h-4 w-4" data-id="element-3180" />,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200'
  }, {
    number: 3,
    title: 'Inhoud & Taalvalidatie',
    description: 'Beoordeelt de didactische kwaliteit en het taalgebruik van de vraag.',
    details: ['Uitleg (explanationText) is aanwezig en voldoende lang (min. 30–100 tekens afhankelijk van tier)', 'Distractor-uitleg aanwezig voor alle antwoordopties (distractorExplanations)', 'Geen verboden vakjargon voor het tier-niveau (bijv. "commutatief" niet bij B6)', 'Vraagstelling niet te lang voor het tier-niveau (max. 200–800 tekens)', 'LaTeX-notatie correct gebruikt voor breuken en formules (Getalbegrip module)'],
    icon: <BookOpenIcon className="h-4 w-4" data-id="element-3181" />,
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200'
  }, {
    number: 4,
    title: 'Support & Hints Validatie',
    description: 'Controleert de aanwezigheid van hints en uitleg voor families die dit vereisen.',
    details: ['Families met hintsRequired=true hebben hints in de familySupportRegistry', 'Families met explanationRequired=true hebben een defaultExplanation', 'Hints geven het antwoord niet direct weg', 'SWW en SPR families vereisen altijd hints (spellingregels moeten worden uitgelegd)', 'Families zonder hints-vereiste worden niet geblokkeerd'],
    icon: <HelpCircleIcon className="h-4 w-4" data-id="element-3182" />,
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200'
  }, {
    number: 5,
    title: 'Visuele Hulpmiddelen Validatie',
    description: 'Valideert visuele componenten voor families die een visual vereisen.',
    details: ['Families in visual.requiredFamilies hebben een stimulusAid of supportAid', 'Visuele typen zijn geregistreerd in het visual registry (visualRegistry.ts)', 'Klok-vragen (TGL-TIJD-ANALOOG) vereisen een clock stimulus visual', 'Meetkunde-families (MET-OPP-*) vereisen een geo support visual', 'Breuk-families (GET-BREUK-*) vereisen een fraction visual'],
    icon: <EyeIcon className="h-4 w-4" data-id="element-3183" />,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200'
  }, {
    number: 6,
    title: 'LDG-Graaf Validatie',
    description: 'Controleert de integriteit van het leerpad-netwerk.',
    details: ['Geen cyclische afhankelijkheden in de leerpad-graaf (A→B→A is verboden)', 'Alle prerequisites verwijzen naar bestaande familyIds in de taxonomy', 'De graaf is een geldige DAG (Directed Acyclic Graph)', 'Families met prerequisites zijn bereikbaar via het leerpad', 'LDG-validatie is alleen actief als featureFlags.ldgValidation=true'],
    icon: <GitBranchIcon className="h-4 w-4" data-id="element-3184" />,
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    borderColor: 'border-pink-200'
  }];
  const cognitiveLevels: LevelCardProps[] = [{
    level: 1,
    name: 'Onthouden',
    description: 'Feiten en begrippen herkennen en reproduceren.',
    example: 'Wat is de hoofdstad van Nederland?',
    color: 'text-gray-600',
    bgColor: 'bg-gray-50'
  }, {
    level: 2,
    name: 'Begrijpen',
    description: 'Betekenis geven aan informatie en uitleggen.',
    example: 'Leg uit wat een breuk is.',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50'
  }, {
    level: 3,
    name: 'Toepassen',
    description: 'Kennis gebruiken in nieuwe situaties.',
    example: 'Bereken 3/4 van 120.',
    color: 'text-teal-600',
    bgColor: 'bg-teal-50'
  }, {
    level: 4,
    name: 'Analyseren',
    description: 'Informatie opsplitsen en verbanden leggen.',
    example: 'Welke breuk is groter en waarom?',
    color: 'text-violet-600',
    bgColor: 'bg-violet-50'
  }, {
    level: 5,
    name: 'Evalueren',
    description: 'Beoordelen en kritisch denken.',
    example: 'Is deze berekening correct? Onderbouw je antwoord.',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50'
  }, {
    level: 6,
    name: 'Creëren',
    description: 'Nieuwe ideeën en producten samenstellen.',
    example: 'Maak een eigen rekenprobleem met breuken.',
    color: 'text-red-600',
    bgColor: 'bg-red-50'
  }];
  const didacticLevels = [{
    level: 1,
    name: 'Oppervlakkig',
    description: 'Directe reproductie van feiten zonder context.',
    color: 'text-gray-600',
    bg: 'bg-gray-100'
  }, {
    level: 2,
    name: 'Procedureel',
    description: 'Stap-voor-stap toepassen van een vaste methode.',
    color: 'text-blue-600',
    bg: 'bg-blue-100'
  }, {
    level: 3,
    name: 'Conceptueel',
    description: 'Begrip van onderliggende concepten en principes.',
    color: 'text-teal-600',
    bg: 'bg-teal-100'
  }, {
    level: 4,
    name: 'Analytisch',
    description: 'Redeneren, vergelijken en verbanden leggen.',
    color: 'text-violet-600',
    bg: 'bg-violet-100'
  }, {
    level: 5,
    name: 'Creatief',
    description: 'Zelfstandig nieuwe oplossingen bedenken.',
    color: 'text-orange-600',
    bg: 'bg-orange-100'
  }];
  const faqs: FaqItemProps[] = [{
    question: 'Wat is het verschil tussen een BLOCKER en een WARNING?',
    answer: 'Een BLOCKER verhindert dat een vraag wordt gebruikt in het systeem. De vraag blijft bestaan maar is geblokkeerd totdat het probleem is opgelost. Een WARNING is een aanbeveling voor verbetering maar blokkeert de vraag niet. Een INFO is puur informatief.'
  }, {
    question: 'Hoe werkt de kwaliteitsscore?',
    answer: 'De kwaliteitsscore is een gewogen gemiddelde van meerdere factoren: aanwezigheid van uitleg en distractor-uitleg, variatie in cognitieve niveaus, didactische diepte, en het ontbreken van verboden patronen. Productie-drempel is 70%, assessment-drempel is 82%.'
  }, {
    question: 'Wat is een LDG-graaf?',
    answer: 'LDG staat voor Leerpad Dependency Graph. Dit is een netwerk dat de afhankelijkheden tussen families vastlegt. Als een leerling family B wil leren maar family A een prerequisite is, dan moet A eerst worden beheerst. De graaf is een DAG (Directed Acyclic Graph) — geen lussen toegestaan.'
  }, {
    question: 'Waarom mag een hint het antwoord niet weggeven?',
    answer: 'Hints zijn bedoeld om de leerling op weg te helpen door de aanpak te beschrijven, niet het antwoord te geven. Als een hint het antwoord direct vermeldt, leert de leerling niets van de vraag. De hint moet de denkstap stimuleren, niet overslaan.'
  }, {
    question: 'Hoe voeg ik een nieuwe family toe aan het systeem?',
    answer: 'Een nieuwe family moet worden geregistreerd in de taxonomy registry (taxonomyRegistry.ts) met een uniek familyId, module, skill, subSkill, tier en levelCode. Vervolgens maak je een seed-bestand aan in src/data/seeds/MODULE/ met minimaal 4 vragen. Optioneel voeg je een entry toe aan familySupportRegistry.ts voor hints en uitleg.'
  }, {
    question: 'Wat is het verschil tussen stimulusAids en supportAids?',
    answer: 'stimulusAids zijn visuele hulpmiddelen die TIJDENS de vraag worden getoond (bijv. een klok bij een klokkijken-vraag). supportAids zijn visuele hulpmiddelen die NA het beantwoorden worden getoond als leerondersteuning (bijv. een breukvisualisatie na een breuk-vraag). Beide zijn arrays van VisualAid objecten.'
  }, {
    question: 'Wat betekent "empty" status in de TaxonomyCoverage?',
    answer: 'Een family met "empty" status heeft geen vragen EN geen support registry entry. Dit zijn de families die nog aangemaakt moeten worden. Er zijn momenteel ~114 families met empty status — dit zijn de prioriteit voor nieuwe seed-bestanden.'
  }];
  return <div className="space-y-6" data-id="element-3185">
      {/* Section Navigation */}
      <div className="flex flex-wrap gap-2 rounded-xl border border-gray-100 bg-gray-50 p-1.5" data-id="element-3186">
        {sections.map(section => <button key={section.id} onClick={() => setActiveSection(section.id)} className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-all ${activeSection === section.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3187">
            {section.icon}
            {section.label}
          </button>)}
      </div>

      {/* Pipeline Section */}
      {activeSection === 'pipeline' && <div className="space-y-4" data-id="element-3188">
          <div className="rounded-xl border border-blue-100 bg-blue-50 p-5" data-id="element-3189">
            <h2 className="text-lg font-bold text-blue-900 mb-2" data-id="element-3190">
              Hoe werkt de validatie-pipeline?
            </h2>
            <p className="text-sm text-blue-800 leading-relaxed" data-id="element-3191">
              Elke vraag doorloopt een reeks van 6 validatiefasen voordat deze
              beschikbaar wordt voor leerlingen. De fasen worden in volgorde
              uitgevoerd. Als een fase een BLOCKER-fout vindt, stopt de pipeline
              en wordt de vraag geblokkeerd. Waarschuwingen worden verzameld
              maar stoppen de pipeline niet.
            </p>
          </div>

          {/* Visual Flow */}
          <div className="flex items-center gap-1 overflow-x-auto pb-2" data-id="element-3192">
            {['Schema', 'Type', 'Inhoud', 'Support', 'Visueel', 'LDG'].map((stage, i) => <div key={stage} className="flex items-center gap-1 flex-shrink-0" data-id="element-3193">
                  <div className="rounded-lg bg-white border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 shadow-sm" data-id="element-3194">
                    {stage}
                  </div>
                  {i < 5 && <ArrowRightIcon className="h-3 w-3 text-gray-400 flex-shrink-0" data-id="element-3195" />}
                </div>)}
            <div className="flex items-center gap-1 flex-shrink-0" data-id="element-3196">
              <ArrowRightIcon className="h-3 w-3 text-gray-400" data-id="element-3197" />
              <div className="rounded-lg bg-emerald-500 px-3 py-2 text-xs font-medium text-white shadow-sm" data-id="element-3198">
                ✓ Gepubliceerd
              </div>
            </div>
          </div>

          <div className="space-y-3" data-id="element-3199">
            {pipelineSteps.map(step => <PipelineStep key={step.number} {...step} data-id="element-3200" />)}
          </div>
        </div>}

      {/* Cognitive Levels Section */}
      {activeSection === 'cognitive' && <div className="space-y-4" data-id="element-3201">
          <div className="rounded-xl border border-violet-100 bg-violet-50 p-5" data-id="element-3202">
            <div className="flex items-start gap-3" data-id="element-3203">
              <BrainIcon className="h-6 w-6 text-violet-600 flex-shrink-0 mt-0.5" data-id="element-3204" />
              <div data-id="element-3205">
                <h2 className="text-lg font-bold text-violet-900 mb-2" data-id="element-3206">
                  Cognitieve Niveaus (Bloom's Taxonomie)
                </h2>
                <p className="text-sm text-violet-800 leading-relaxed" data-id="element-3207">
                  Het systeem gebruikt de taxonomie van Bloom om het denkniveau
                  van vragen te classificeren. Niveau 1 is het eenvoudigst
                  (feiten onthouden), niveau 6 is het meest complex (creatief
                  denken). Een goede vragenset heeft een gevarieerde verdeling
                  over meerdere niveaus.
                </p>
              </div>
            </div>
          </div>

          {/* Visual pyramid */}
          <div className="rounded-xl border border-gray-100 bg-white p-5" data-id="element-3208">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4" data-id="element-3209">
              Bloom's Piramide
            </h3>
            <div className="space-y-1.5" data-id="element-3210">
              {[...cognitiveLevels].reverse().map(level => <div key={level.level} className={`rounded-lg ${level.bgColor} px-4 py-2 flex items-center gap-3`} style={{
            marginLeft: `${(6 - level.level) * 16}px`
          }} data-id="element-3211">
                  <span className={`text-xs font-bold ${level.color} w-4`} data-id="element-3212">
                    {level.level}
                  </span>
                  <span className={`text-sm font-semibold ${level.color}`} data-id="element-3213">
                    {level.name}
                  </span>
                  <span className="text-xs text-gray-500 hidden sm:block" data-id="element-3214">
                    {level.description}
                  </span>
                </div>)}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-id="element-3215">
            {cognitiveLevels.map(level => <CognitiveLevelCard key={level.level} {...level} data-id="element-3216" />)}
          </div>
        </div>}

      {/* Didactic Depth Section */}
      {activeSection === 'didactic' && <div className="space-y-4" data-id="element-3217">
          <div className="rounded-xl border border-teal-100 bg-teal-50 p-5" data-id="element-3218">
            <div className="flex items-start gap-3" data-id="element-3219">
              <LayersIcon className="h-6 w-6 text-teal-600 flex-shrink-0 mt-0.5" data-id="element-3220" />
              <div data-id="element-3221">
                <h2 className="text-lg font-bold text-teal-900 mb-2" data-id="element-3222">
                  Didactische Diepte
                </h2>
                <p className="text-sm text-teal-800 leading-relaxed" data-id="element-3223">
                  Didactische diepte beschrijft hoe diep een vraag ingaat op de
                  leerstof. Dit verschilt van het cognitieve niveau: een vraag
                  kan procedureel zijn (niveau 2) maar toch een hoog cognitief
                  niveau vereisen. Samen geven deze twee dimensies een volledig
                  beeld van de vraagkwaliteit.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3" data-id="element-3224">
            {didacticLevels.map(level => <div key={level.level} className={`rounded-xl border border-gray-100 ${level.bg} p-4`} data-id="element-3225">
                <div className="flex items-start gap-4" data-id="element-3226">
                  <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-white shadow-sm font-bold ${level.color}`} data-id="element-3227">
                    {level.level}
                  </div>
                  <div data-id="element-3228">
                    <h3 className={`font-semibold ${level.color} mb-1`} data-id="element-3229">
                      {level.name}
                    </h3>
                    <p className="text-sm text-gray-700" data-id="element-3230">{level.description}</p>
                  </div>
                </div>
              </div>)}
          </div>

          <div className="rounded-xl border border-gray-100 bg-white p-5" data-id="element-3231">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2" data-id="element-3232">
              <TrendingUpIcon className="h-4 w-4 text-teal-600" data-id="element-3233" />
              Cognitief niveau vs. Didactische diepte
            </h3>
            <div className="overflow-x-auto" data-id="element-3234">
              <table className="w-full text-sm" data-id="element-3235">
                <thead data-id="element-3236">
                  <tr className="border-b border-gray-100" data-id="element-3237">
                    <th className="text-left py-2 pr-4 text-gray-500 font-medium" data-id="element-3238">
                      Dimensie
                    </th>
                    <th className="text-left py-2 pr-4 text-gray-500 font-medium" data-id="element-3239">
                      Wat meet het?
                    </th>
                    <th className="text-left py-2 text-gray-500 font-medium" data-id="element-3240">
                      Schaal
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-3241">
                  <tr className="border-b border-gray-50" data-id="element-3242">
                    <td className="py-3 pr-4 font-medium text-violet-700" data-id="element-3243">
                      Cognitief niveau
                    </td>
                    <td className="py-3 pr-4 text-gray-600" data-id="element-3244">
                      Het denkniveau dat de vraag vereist
                    </td>
                    <td className="py-3 text-gray-600" data-id="element-3245">1–6 (Bloom)</td>
                  </tr>
                  <tr data-id="element-3246">
                    <td className="py-3 pr-4 font-medium text-teal-700" data-id="element-3247">
                      Didactische diepte
                    </td>
                    <td className="py-3 pr-4 text-gray-600" data-id="element-3248">
                      Hoe diep de vraag ingaat op de leerstof
                    </td>
                    <td className="py-3 text-gray-600" data-id="element-3249">1–5</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>}

      {/* LDG Section */}
      {activeSection === 'ldg' && <div className="space-y-4" data-id="element-3250">
          <div className="rounded-xl border border-pink-100 bg-pink-50 p-5" data-id="element-3251">
            <div className="flex items-start gap-3" data-id="element-3252">
              <GitBranchIcon className="h-6 w-6 text-pink-600 flex-shrink-0 mt-0.5" data-id="element-3253" />
              <div data-id="element-3254">
                <h2 className="text-lg font-bold text-pink-900 mb-2" data-id="element-3255">
                  Leerpad Dependency Graph (LDG)
                </h2>
                <p className="text-sm text-pink-800 leading-relaxed" data-id="element-3256">
                  De LDG is een gerichte acyclische graaf (DAG) die de
                  afhankelijkheden tussen vaardigheden vastlegt. Het systeem
                  gebruikt deze graaf om te bepalen welke vaardigheden een
                  leerling al moet beheersen voordat een nieuwe vaardigheid
                  wordt aangeboden.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-3257">
            <div className="rounded-xl border border-gray-100 bg-white p-5" data-id="element-3258">
              <h3 className="font-semibold text-gray-800 mb-3" data-id="element-3259">
                Kernconcepten
              </h3>
              <div className="space-y-3" data-id="element-3260">
                <div className="flex items-start gap-3" data-id="element-3261">
                  <div className="h-2 w-2 rounded-full bg-pink-500 mt-2 flex-shrink-0" data-id="element-3262" />
                  <div data-id="element-3263">
                    <p className="text-sm font-medium text-gray-700" data-id="element-3264">
                      Knoop (Node)
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-3265">
                      Een vaardigheid in het leerpad
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-3266">
                  <div className="h-2 w-2 rounded-full bg-pink-500 mt-2 flex-shrink-0" data-id="element-3267" />
                  <div data-id="element-3268">
                    <p className="text-sm font-medium text-gray-700" data-id="element-3269">
                      Kant (Edge)
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-3270">
                      Een afhankelijkheid tussen twee vaardigheden
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-3271">
                  <div className="h-2 w-2 rounded-full bg-pink-500 mt-2 flex-shrink-0" data-id="element-3272" />
                  <div data-id="element-3273">
                    <p className="text-sm font-medium text-gray-700" data-id="element-3274">
                      Prerequisite
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-3275">
                      Een vaardigheid die eerst beheerst moet worden
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-3276">
                  <div className="h-2 w-2 rounded-full bg-pink-500 mt-2 flex-shrink-0" data-id="element-3277" />
                  <div data-id="element-3278">
                    <p className="text-sm font-medium text-gray-700" data-id="element-3279">DAG</p>
                    <p className="text-xs text-gray-500" data-id="element-3280">
                      Directed Acyclic Graph — geen lussen toegestaan
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-100 bg-white p-5" data-id="element-3281">
              <h3 className="font-semibold text-gray-800 mb-3" data-id="element-3282">
                Validatieregels
              </h3>
              <div className="space-y-3" data-id="element-3283">
                <div className="flex items-start gap-3" data-id="element-3284">
                  <CheckCircleIcon className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" data-id="element-3285" />
                  <p className="text-sm text-gray-700" data-id="element-3286">
                    Geen cyclische afhankelijkheden (A→B→A is verboden)
                  </p>
                </div>
                <div className="flex items-start gap-3" data-id="element-3287">
                  <CheckCircleIcon className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" data-id="element-3288" />
                  <p className="text-sm text-gray-700" data-id="element-3289">
                    Alle prerequisite-IDs moeten bestaan
                  </p>
                </div>
                <div className="flex items-start gap-3" data-id="element-3290">
                  <CheckCircleIcon className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" data-id="element-3291" />
                  <p className="text-sm text-gray-700" data-id="element-3292">
                    Elke vaardigheid is bereikbaar vanuit het startpunt
                  </p>
                </div>
                <div className="flex items-start gap-3" data-id="element-3293">
                  <CheckCircleIcon className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" data-id="element-3294" />
                  <p className="text-sm text-gray-700" data-id="element-3295">
                    De graaf is een geldige DAG-structuur
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Simple visual example */}
          <div className="rounded-xl border border-gray-100 bg-white p-5" data-id="element-3296">
            <h3 className="font-semibold text-gray-800 mb-4" data-id="element-3297">
              Voorbeeld: Breuken Leerpad
            </h3>
            <div className="flex flex-wrap items-center gap-3 text-sm" data-id="element-3298">
              <div className="rounded-lg bg-gray-100 px-3 py-2 text-gray-700 font-medium" data-id="element-3299">
                Getallen lezen
              </div>
              <ArrowRightIcon className="h-4 w-4 text-gray-400" data-id="element-3300" />
              <div className="rounded-lg bg-blue-100 px-3 py-2 text-blue-700 font-medium" data-id="element-3301">
                Breuken begrijpen
              </div>
              <ArrowRightIcon className="h-4 w-4 text-gray-400" data-id="element-3302" />
              <div className="rounded-lg bg-violet-100 px-3 py-2 text-violet-700 font-medium" data-id="element-3303">
                Breuken optellen
              </div>
              <ArrowRightIcon className="h-4 w-4 text-gray-400" data-id="element-3304" />
              <div className="rounded-lg bg-emerald-100 px-3 py-2 text-emerald-700 font-medium" data-id="element-3305">
                Gemengde getallen
              </div>
            </div>
            <p className="mt-3 text-xs text-gray-500" data-id="element-3306">
              Een leerling kan pas "Breuken optellen" leren als "Breuken
              begrijpen" is beheerst.
            </p>
          </div>
        </div>}

      {/* FAQ Section */}
      {activeSection === 'faq' && <div className="space-y-4" data-id="element-3307">
          <div className="rounded-xl border border-amber-100 bg-amber-50 p-5" data-id="element-3308">
            <div className="flex items-start gap-3" data-id="element-3309">
              <HelpCircleIcon className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-3310" />
              <div data-id="element-3311">
                <h2 className="text-lg font-bold text-amber-900 mb-1" data-id="element-3312">
                  Veelgestelde Vragen
                </h2>
                <p className="text-sm text-amber-800" data-id="element-3313">
                  Antwoorden op de meest gestelde vragen over het
                  governance-systeem.
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-100 bg-white px-5 divide-y divide-gray-100" data-id="element-3314">
            {faqs.map((faq, i) => <FaqItem key={i} {...faq} data-id="element-3315" />)}
          </div>
        </div>}
    </div>;
}